import { Flex, Typography } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { Image, Input, Radio, Row, Col, Spin } from "antd";
import { toast } from "react-toastify";
import { sellBoostFetch } from "../../utlifyFunction";
import GeneratedDescriptionCard from "../../components/Cards/GeneratedDescriptionCard";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import Keywords from "../../components/Cards/Keywords";
import AIConfiguration from "../../components/Cards/AIConfiguration";
import { DataContext } from "../../contexts/DataContext";
import CustomInstructions from "../../components/Cards/CustomInstructions";
import CustomCard from "../../components/Cards/CustomCard";


const { Paragraph } = Typography;

export default function Description() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { creditHandlers } = useContext(DataContext);
	const { fetchCredits } = creditHandlers;
	const [selectedProduct, setSelectedProduct] = useState({});
	const [newDescription, setNewDescription] = useState("");
	const [keywords, setKeywords] = useState("");
	const [tone, setTone] = useState("Professional");
	const [maxWords, setMaxWords] = useState(50);
	const [productType, setProductType] = useState("Other");
	const [ctaType, setCtaType] = useState("Urgency");
	const [targetAudience, setTargetAudience] = useState("");

	const [isButtonLoading, setIsButtonLoading] = useState(false);
	const [showAIInputs, setShowAIInputs] = useState(false);
	const [numberOfKeywords, setNumberOfKeywords] = useState("");
	const [keywordCountry, setKeywordCountry] = useState("");
	const [keywordTopic, setKeywordTopic] = useState("");
	const [loading, setLoading] = useState(false);
	const [customPrompt, setCustomPrompt] = useState("");

	const [activeComponent, setActiveComponent] = useState("instructions");

	const handleGenerate = async () => {
		setIsButtonLoading(true);
		try {
			const response = await sellBoostFetch("POST", "/AI/generate", {
				title: selectedProduct?.title || "",
				existingDescription: selectedProduct?.descriptionHtml || null,
				keywords,
				tone,
				maxWords,
				targetAudience,
				productType,
				ctaType,
				customPrompt,
				mode: activeComponent,
			});
			if (response?.status === 200) {
				setNewDescription(response?.data?.description || "");
				toast.success("Description generated successfully!");
			}
		} catch (error) {
			toast.error("Failed to generate description.");
		} finally {
			setIsButtonLoading(false);
		}
	};

	const handleGenerateKeywords = async () => {
		setLoading(true);
		try {
			const response = await sellBoostFetch("POST", "/AI/getTrendingKeywords", {
				numberOfKeywords,
				country: keywordCountry,
				topicRelatedTo: keywordTopic,
			});
			if (response?.keywords) {
				setKeywords(response.keywords.join(",").replaceAll('"'));
				toast.success("Keywords generated successfully!");
			}
		} catch (error) {
			toast.error("Failed to generate keywords.");
		} finally {
			setLoading(false);
		}
	};

	const handleProductUpdate = async () => {
		if (!newDescription) {
			toast.error("Enter a new description or generate one.");
			return;
		}
		setLoading(true);
		try {
			const response = await sellBoostFetch("POST", "/shopify/updateProduct", {
				productId: selectedProduct?.id,
				description: newDescription,
				oldDescription: selectedProduct?.descriptionHtml,
			});
			if (response?.status === 200) {
				toast.success("Product description updated!");
				fetchProductById();
				fetchCredits();
			}
		} catch (error) {
			toast.error("Failed to update product description.");
			setLoading(false);
		}
	};

	const fetchProductById = async () => {
		setLoading(true);
		const data = await sellBoostFetch("GET", "/shopify/fetchProduct/" + id);
		if (data?.status === 200) {
			console.log(data);
			setSelectedProduct(data?.data);
		} else {
			navigate("/products");
		}
		setLoading(false);
	};
	useEffect(() => {
		fetchProductById();
	}, [id]);
	return (
		<div>
			<Typography.Title level={4} style={{ margin: "10px 0px" }}>
				<span
					onClick={() => window.history.back()}
					style={{ cursor: "pointer", marginRight: "8px" }}
				>
					←
				</span>
				{selectedProduct?.title}
			</Typography.Title>

			<Spin spinning={isButtonLoading || loading}>
				<Flex justify="space-between" >
					<Col span={15}>
						<Row gutter={[16, 16]}>
							<Col span={24}>
								<CustomCard>
									<Image
										src={selectedProduct?.images?.edges[0]?.node?.originalSrc}
										alt={selectedProduct?.title}
										style={{
											height: 50,
											objectFit: "cover",
											marginBottom: 16,
										}}
									/>
									<Paragraph>Existing Description:</Paragraph>
									<Input.TextArea
										value={
											selectedProduct?.descriptionHtml ||
											"No description available."
										}
										rows={3}
										disabled
									/>
								</CustomCard>
							</Col>

							<Col span={24}>
								<Keywords
									keywords={keywords}
									setKeywords={setKeywords}
									loading={loading}
									showAIInputs={showAIInputs}
									setShowAIInputs={setShowAIInputs}
									numberOfKeywords={numberOfKeywords}
									setNumberOfKeywords={setNumberOfKeywords}
									keywordCountry={keywordCountry}
									setKeywordCountry={setKeywordCountry}
									keywordTopic={keywordTopic}
									setKeywordTopic={setKeywordTopic}
									isButtonLoading={isButtonLoading}
									handleGenerateKeywords={handleGenerateKeywords}
								/>
							</Col>

							<Col span={24}>
								<Radio.Group
									value={activeComponent}
									onChange={(e) => setActiveComponent(e.target.value)}
									buttonStyle="solid"
								>
									<Radio.Button value="configuration">
										Use Smart Fields (Recommended)
									</Radio.Button>
									<Radio.Button value="instructions">
										Write Custom Prompt
									</Radio.Button>
								</Radio.Group>
							</Col>

							<Col span={24}>
								<AnimatePresence mode="wait">
									<motion.div
										key={activeComponent}
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										transition={{ duration: 0.2 }}
									>
										{activeComponent === "instructions" ? (
											<CustomInstructions
												maxWords={maxWords}
												loading={loading}
												setLoading={setLoading}
												customPrompt={customPrompt}
												setCustomPrompt={setCustomPrompt}
												title={selectedProduct?.title || ""}
												existingDescription={
													selectedProduct?.descriptionHtml || null
												}
											/>
										) : (
											<AIConfiguration
												tone={tone}
												setTone={setTone}
												maxWords={maxWords}
												setMaxWords={setMaxWords}
												targetAudience={targetAudience}
												setTargetAudience={setTargetAudience}
												productType={productType}
												setProductType={setProductType}
												ctaType={ctaType}
												setCtaType={setCtaType}
											/>
										)}
									</motion.div>
								</AnimatePresence>
							</Col>
							<Col span={24}>
								<PrimaryButton
									onClick={handleGenerate}
									loading={isButtonLoading}
									width={"100%"}
								>
									Generate AI Description
								</PrimaryButton>
							</Col>
						</Row>
					</Col>
					<Col span={8}>
						<CustomCard>
							<Paragraph style={{ fontWeight: "500" }}>
								Generated Descriptions:
							</Paragraph>
							<Flex vertical gap={"24px"}>
								{/* {[0, 0, 0]?.map((item, index) => {
								return ( */}
								<GeneratedDescriptionCard
									handleProductUpdate={handleProductUpdate}
									newDescription={newDescription}
									setNewDescription={setNewDescription}
								/>
								{/* );
							})} */}
							</Flex>
						</CustomCard>
					</Col>
				</Flex>
			</Spin>
		</div>
	);
}
